import { ResponsiveLine } from "@nivo/line";
import { nFormatter } from "~/utils/formatNumbers";
import { BRAND_PRIMARY_RGB, BRAND_SECONDARY_RGB } from "~/brandColors";
import { useCurrency } from "~/hooks/useCurrency";
import type { Language } from "~/utils/language";

export interface RetirementGraphData {
  id: string;
  color: string;
  data: {
    x: number;
    y: number;
  }[];
}

interface retirementGraphProps {
  dataGraphs: RetirementGraphData[];
  retirementAmount: number;
  retirementYear: number;
  lang?: Language;
}

const tooltipLabels = {
  HEB: {
    today: "כיום",
    nextYear: "שנה הבאה",
    inYears: (n: string) => `עוד ${n} שנים`,
  },
  ENG: {
    today: "Today",
    nextYear: "Next Year",
    inYears: (n: string) => `In ${n} years`,
  },
  RUS: {
    today: "Сейчас",
    nextYear: "Следующий год",
    inYears: (n: string) => `Через ${n} лет`,
  },
};

const axisLabels = {
  HEB: { years: "שנים" },
  ENG: { years: "Years" },
  RUS: { years: "Годы" },
};

export const RetirementGraph = ({
  dataGraphs,
  retirementAmount,
  retirementYear,
  lang = "HEB",
}: retirementGraphProps) => {
  const isEng = lang === "ENG";
  const {
    symbol: currencySymbol,
    convert: convertCurr,
    unconvert: unconvertCurr,
  } = useCurrency();
  let tickValues = reduceTickValues(dataGraphs);

  const rawMin = findMinPoint(dataGraphs);
  const rawMax = findMaxPoint(dataGraphs);
  const yTickValues = computeNiceYTicks(
    convertCurr(rawMin),
    convertCurr(rawMax),
    unconvertCurr,
    7
  );

  return (
    <div className="h-[350px] min-h-[300px] w-full min-w-[200px] mobile:h-[350px]">
      <ResponsiveLine
        margin={{
          top: 20,
          right: 10,
          bottom: isEng ? 50 : 40,
          left: isEng ? 50 : 40,
        }}
        animate={true}
        enableSlices={"x"}
        markers={[
          {
            axis: "y",
            lineStyle: {
              stroke: "#b0413e",
              strokeWidth: 1,
            },
            value: retirementAmount,
          },
          // {
          //     axis: 'x',
          //     lineStyle: {
          //       stroke: '#b0413e',
          //       strokeWidth: 1
          //     },
          //     value: retirementYear
          // }
        ]}
        sliceTooltip={({ slice }) => {
          return (
            <div
              style={{
                background: "white",
                padding: "9px 12px",
                border: "1px solid #ccc",
              }}
            >
              <div>
                <strong>
                  {slice.points[0].data.xFormatted == "0"
                    ? tooltipLabels[lang].today
                    : slice.points[0].data.xFormatted == "1"
                    ? tooltipLabels[lang].nextYear
                    : tooltipLabels[lang].inYears(
                        String(slice.points[0].data.xFormatted)
                      )}
                </strong>
              </div>
              {slice.points.map((point) => (
                <div
                  key={point.id}
                  style={{
                    color: point.serieColor,
                    padding: "3px 0",
                  }}
                >
                  <strong>{point.serieId}</strong> {point.data.yFormatted}
                </div>
              ))}
            </div>
          );
        }}
        enablePoints={false}
        colors={[BRAND_SECONDARY_RGB, BRAND_PRIMARY_RGB]}
        data={dataGraphs}
        enableGridX={false}
        yScale={{
          type: "linear",
          min: Math.min(yTickValues[0], rawMin),
          max: Math.max(yTickValues[yTickValues.length - 1], rawMax),
        }}
        yFormat={(v: any) => {
          const num = typeof v === "number" ? v : parseFloat(v as string);
          const converted = convertCurr(num);
          return `${currencySymbol}${nFormatter(
            Math.round(Math.abs(converted)),
            3
          )}${converted < 0 ? "-" : ""}`;
        }}
        // xFormat={v => {
        //     let currentYear = new Date().getFullYear();
        //     return `${v - currentYear}`
        // }
        // }
        useMesh={true}
        enableArea={true}
        areaBaselineValue={findMinPoint(dataGraphs)}
        axisLeft={{
          tickValues: yTickValues,
          tickSize: 0,
          tickPadding: isEng ? 5 : 38,
          tickRotation: 0,
          format: (v: any) => {
            const num = typeof v === "number" ? v : parseFloat(v as string);
            const converted = convertCurr(num);
            return `${currencySymbol}${nFormatter(
              Math.round(Math.abs(converted)),
              3
            )}${converted < 0 ? "-" : ""}`;
          },
        }}
        axisBottom={{
          tickValues: tickValues,
          tickSize: 2,
          tickPadding: 4,
          legend: axisLabels[lang].years,
          legendPosition: "middle",
          legendOffset: isEng ? -15 : -10,
        }}
        // valueFormat={v => `₪${formatNumber(Math.round(v).toString())}`}
        legends={[
          {
            anchor: "top-left",
            direction: "row",
            justify: isEng ? false : true,
            translateY: -20,

            itemsSpacing: isEng ? 10 : 100,
            itemDirection: "left-to-right",

            itemWidth: isEng ? 150 : 20,
            itemHeight: 20,
            itemOpacity: 0.75,
            symbolSize: 12,
            symbolShape: "circle",
            symbolBorderColor: "rgba(0, 0, 0, .5)",
            effects: [
              {
                on: "hover",
                style: {
                  itemBackground: "rgba(0, 0, 0, .03)",
                  itemOpacity: 1,
                },
              },
            ],
          },
        ]}
      />
    </div>
  );
};

export function findMinPoint(data: RetirementGraphData[]): number {
  let min = data[0].data[0].y;
  data.forEach((graph) => {
    graph.data.forEach((point) => {
      if (point.y < min) {
        min = point.y;
      }
    });
  });
  return min;
}

export function findMaxPoint(data: RetirementGraphData[]): number {
  let max = data[0].data[0].y;
  data.forEach((graph) => {
    graph.data.forEach((point) => {
      if (point.y > max) {
        max = point.y;
      }
    });
  });
  return max;
}

function reduceTickValues(data: RetirementGraphData[]): number[] {
  let tickValues = [];
  let max = 0;
  data.forEach((graph) => {
    if (graph.data)
      graph.data.forEach((point) => {
        if (point.x > max) {
          max = point.x;
        }
      });
  });
  let interval = Math.ceil(max / 8);
  for (let i = 0; i < 8; i++) {
    if (i * interval > max) {
      break;
    }
    tickValues.push(i * interval);
  }
  return tickValues;
}

/**
 * Compute evenly-spaced "nice" tick values (round numbers like 100k, 200k)
 * in the display currency, then convert back to ILS for nivo positioning.
 */
export function computeNiceYTicks(
  displayMin: number,
  displayMax: number,
  unconvert: (v: number) => number,
  numTicks: number = 6
): number[] {
  const lo = Math.min(displayMin, displayMax);
  const hi = Math.max(displayMin, displayMax);
  const range = hi - lo;
  if (range <= 0) return [unconvert(0)];

  const roughStep = range / (numTicks - 1);
  const mag = Math.pow(10, Math.floor(Math.log10(roughStep)));
  const frac = roughStep / mag;

  let step: number;
  if (frac <= 1.5) step = mag;
  else if (frac <= 3.5) step = 2 * mag;
  else if (frac <= 7.5) step = 5 * mag;
  else step = 10 * mag;

  const niceMin = Math.floor(lo / step) * step;
  const niceMax = Math.ceil(hi / step) * step;

  const ticks: number[] = [];
  for (let v = niceMin; v <= niceMax + step * 0.001; v += step) {
    ticks.push(unconvert(v));
  }
  return ticks;
}
